/**
 * Regression Line (Updated for Scrolling)
 * The core mechanic: y = mx + b with variance for uncertainty
 * Now scrolls with the camera in the infinite runner style
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { lerp, fractalNoise } from '../systems/Utils.js';

export class RegressionLine {
    constructor(scene) {
        this.scene = scene;

        // Line equation: y = mx + b
        this.slope = 0;
        this.targetSlope = 0;
        // Start intercept at vertical center of screen
        const centerY = scene.scale.height / 2;
        this.intercept = centerY;
        this.targetIntercept = centerY;

        // Variance system (uncertainty)
        this.variance = CONSTANTS.LINE_VARIANCE_BASE;
        this.maxVariance = CONSTANTS.LINE_VARIANCE_MAX;

        // Lerp rate (can be overridden for boss fight)
        this.lerpRate = CONSTANTS.LINE_LERP_RATE;

        // Noise seed for consistent variance
        this.noiseSeed = Math.random() * 1000;
        this.noiseTime = 0;

        // Slope change timing
        this.slopeChangeTimer = 0;
        this.slopeChangeInterval = 5000;

        // Graphics
        this.graphics = scene.add.graphics().setDepth(10);
        this.glowGraphics = scene.add.graphics().setDepth(9);

        // Current camera X for rendering
        this.cameraX = 0;

        // Line tolerance for "on line" detection
        this.lineTolerance = 30;
    }

    // Get Y position at given X (with optional variance)
    getYAtX(x, includeVariance = true) {
        // Use relative X for slope calculation to keep line centered
        const relativeX = x - this.cameraX;
        const baseY = this.slope * relativeX + this.intercept;

        if (!includeVariance) return baseY;

        // Add procedural noise based on variance
        const noise = this.getNoiseAtX(x);
        return baseY + noise * this.variance * 80;
    }

    // Get noise value at X position (absolute X for consistency)
    getNoiseAtX(x) {
        const frequency = 0.008;
        const noise = fractalNoise(
            x * frequency + this.noiseTime,
            3,
            0.5,
            this.noiseSeed
        );
        return (noise - 0.5) * 2;
    }

    // Check if a point is on the true line (within tolerance)
    isOnLine(x, y, tolerance = null) {
        tolerance = tolerance || this.lineTolerance;
        const lineY = this.getYAtX(x);
        return Math.abs(y - lineY) < tolerance;
    }

    // Get distance from line at point
    getDistanceFromLine(x, y) {
        const lineY = this.getYAtX(x);
        return Math.abs(y - lineY);
    }

    update(delta, cameraX = 0) {
        this.cameraX = cameraX;

        // Update noise time for animated variance
        this.noiseTime += delta * 0.0003;

        // Lerp toward target (higher = faster response = harder)
        this.slope = lerp(this.slope, this.targetSlope, this.lerpRate);
        this.intercept = lerp(this.intercept, this.targetIntercept, this.lerpRate);

        // Render
        this.render();
    }

    randomizeSlope() {
        this.targetSlope = Phaser.Math.FloatBetween(
            CONSTANTS.LINE_SLOPE_MIN,
            CONSTANTS.LINE_SLOPE_MAX
        );

        if (this.scene.onSlopeChange) {
            this.scene.onSlopeChange(this.targetSlope);
        }
    }

    render() {
        this.graphics.clear();
        this.glowGraphics.clear();

        const startX = this.cameraX;
        const endX = this.cameraX + this.scene.scale.width;

        // Glow layer (wider, softer)
        this.glowGraphics.lineStyle(20, CONSTANTS.COLORS.LINE_GLOW, 0.1);
        this.glowGraphics.beginPath();

        // Secondary glow
        this.graphics.lineStyle(10, CONSTANTS.COLORS.LINE_GLOW, 0.2);
        this.graphics.beginPath();

        let firstPoint = true;
        for (let x = startX; x <= endX; x += 4) {
            const y = this.getYAtX(x);

            if (firstPoint) {
                this.glowGraphics.moveTo(x, y);
                firstPoint = false;
            } else {
                this.glowGraphics.lineTo(x, y);
            }
        }

        this.glowGraphics.strokePath();

        // Main line
        this.graphics.lineStyle(4, CONSTANTS.COLORS.LINE_TRUE, 0.9);
        this.graphics.beginPath();

        firstPoint = true;
        for (let x = startX; x <= endX; x += 4) {
            const y = this.getYAtX(x);

            if (firstPoint) {
                this.graphics.moveTo(x, y);
                firstPoint = false;
            } else {
                this.graphics.lineTo(x, y);
            }
        }

        this.graphics.strokePath();

        // Draw tolerance zone indicator (subtle)
        this.drawToleranceZone(startX, endX);
    }

    drawToleranceZone(startX, endX) {
        // Safe zone (green-ish fill) - TIGHTER for harder gameplay
        const SAFE_ZONE = 30;
        const DANGER_ZONE = 70;

        // Draw safe zone as filled area
        this.glowGraphics.fillStyle(0x00FF00, 0.08);
        this.glowGraphics.beginPath();

        // Top edge of safe zone
        let firstPoint = true;
        for (let x = startX; x <= endX; x += 10) {
            const y = this.getYAtX(x) - SAFE_ZONE;
            if (firstPoint) {
                this.glowGraphics.moveTo(x, y);
                firstPoint = false;
            } else {
                this.glowGraphics.lineTo(x, y);
            }
        }
        // Bottom edge (reverse direction)
        for (let x = endX; x >= startX; x -= 10) {
            const y = this.getYAtX(x) + SAFE_ZONE;
            this.glowGraphics.lineTo(x, y);
        }
        this.glowGraphics.closePath();
        this.glowGraphics.fillPath();

        // Danger zone boundaries (dashed red-ish)
        this.glowGraphics.lineStyle(1, 0xFF4444, 0.3);

        // Upper danger boundary
        this.glowGraphics.beginPath();
        firstPoint = true;
        for (let x = startX; x <= endX; x += 20) {
            const y = this.getYAtX(x) - DANGER_ZONE;
            if (firstPoint) {
                this.glowGraphics.moveTo(x, y);
                firstPoint = false;
            } else {
                this.glowGraphics.lineTo(x, y);
            }
        }
        this.glowGraphics.strokePath();

        // Lower danger boundary
        this.glowGraphics.beginPath();
        firstPoint = true;
        for (let x = startX; x <= endX; x += 20) {
            const y = this.getYAtX(x) + DANGER_ZONE;
            if (firstPoint) {
                this.glowGraphics.moveTo(x, y);
                firstPoint = false;
            } else {
                this.glowGraphics.lineTo(x, y);
            }
        }
        this.glowGraphics.strokePath();

        // Safe zone boundaries (green)
        this.glowGraphics.lineStyle(1, 0x44FF44, 0.4);

        // Upper safe boundary
        this.glowGraphics.beginPath();
        firstPoint = true;
        for (let x = startX; x <= endX; x += 10) {
            const y = this.getYAtX(x) - SAFE_ZONE;
            if (firstPoint) {
                this.glowGraphics.moveTo(x, y);
                firstPoint = false;
            } else {
                this.glowGraphics.lineTo(x, y);
            }
        }
        this.glowGraphics.strokePath();

        // Lower safe boundary
        this.glowGraphics.beginPath();
        firstPoint = true;
        for (let x = startX; x <= endX; x += 10) {
            const y = this.getYAtX(x) + SAFE_ZONE;
            if (firstPoint) {
                this.glowGraphics.moveTo(x, y);
                firstPoint = false;
            } else {
                this.glowGraphics.lineTo(x, y);
            }
        }
        this.glowGraphics.strokePath();
    }

    setSlope(newSlope) {
        this.targetSlope = Phaser.Math.Clamp(
            newSlope,
            CONSTANTS.LINE_SLOPE_MIN,
            CONSTANTS.LINE_SLOPE_MAX
        );
    }

    // Set target regression from data points (called by DataPointManager)
    setTargetRegression(slope, intercept) {
        // Clamp slope - wider range = harder gameplay
        const clampedSlope = Phaser.Math.Clamp(slope, CONSTANTS.LINE_SLOPE_MIN, CONSTANTS.LINE_SLOPE_MAX);

        // Clamp intercept to keep line on screen
        const clampedIntercept = Phaser.Math.Clamp(intercept, 150, this.scene.scale.height - 150);

        // SMOOTH transition - don't jump, lerp toward target
        // Only update target, actual values lerp in update()
        this.targetSlope = clampedSlope;
        this.targetIntercept = clampedIntercept;
    }

    increaseVariance(amount) {
        this.variance = Math.min(
            this.variance + amount,
            this.maxVariance
        );
    }

    getVarianceLevel() {
        return (this.variance - CONSTANTS.LINE_VARIANCE_BASE) /
            (this.maxVariance - CONSTANTS.LINE_VARIANCE_BASE);
    }

    getNearestPointOnLine(x, y) {
        const lineY = this.getYAtX(x);
        return { x: x, y: lineY };
    }

    destroy() {
        this.graphics.destroy();
        this.glowGraphics.destroy();
    }
}
